// Correlation/identity IDs for the Fintepla frontend analytics layer.
//
//   analytics_session_id — per browser session (sessionStorage).
//                          Rotate on explicit "restart conversation" intents.
//   anonymous_user_id    — stable across sessions (localStorage).
//   client_action_id     — per meaningful user action that must correlate
//                          with a backend bot event (e.g. Speak to a Person).
//
// All three are UUIDs generated via `crypto.randomUUID` with a Math.random
// fallback for non-secure contexts / very old runtimes.

import {
  clearSession,
  getLocal,
  getSession,
  setLocal,
  setSession,
} from './storage.js'

const SESSION_KEY = 'fintepla_analytics_session_id'
const USER_KEY = 'fintepla_anonymous_user_id'

export function uuid() {
  try {
    const c = typeof globalThis !== 'undefined' ? globalThis.crypto : null
    if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  } catch {
    // fall through
  }
  // RFC4122-ish v4 fallback. Not cryptographically strong — used only
  // when crypto.randomUUID is unavailable.
  const rnd = (n) => Math.floor(Math.random() * n)
  const hex = (n) => rnd(16).toString(16).padStart(n, '0')
  const b = () => hex(1)
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${(8 + rnd(4)).toString(16)}${hex(3)}-${b()}${b()}${b()}${b()}${b()}${b()}${b()}${b()}${b()}${b()}${b()}${b()}`
}

export function getAnalyticsSessionId() {
  let id = getSession(SESSION_KEY)
  if (!id) {
    id = uuid()
    setSession(SESSION_KEY, id)
  }
  return id
}

/** Drop and re-generate `analytics_session_id`. Use when the user explicitly
 *  restarts the conversation so the backend can treat the next turn as a
 *  fresh journey. */
export function rotateAnalyticsSessionId() {
  clearSession(SESSION_KEY)
  return getAnalyticsSessionId()
}

export function getAnonymousUserId() {
  let id = getLocal(USER_KEY)
  if (!id) {
    id = uuid()
    setLocal(USER_KEY, id)
  }
  return id
}

/** Fresh UUID for per-action correlation. Not persisted. */
export function newClientActionId() {
  return uuid()
}

